/**
 * Azure DevOps Integration Settings Page
 * Sprint 4 - ADO Integration (P0)
 * 
 * Tabs:
 * - Connection: OAuth connect/disconnect, status display
 * - Project Mapping: Map ADO projects to ScopeOps projects
 * - Sync History: View sync logs and metrics
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Cloud, Link2, Unlink, RefreshCw, Check, X, AlertCircle, 
  FolderSync, Clock, ChevronRight, ChevronDown, Play, Settings2, History,
  Building2, Folder, ExternalLink, Loader2, Info
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Tab Component
const Tab = ({ active, onClick, children, icon: Icon }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
      active 
        ? 'border-teal-500 text-teal-600' 
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
    }`}
  >
    {Icon && <Icon className="w-4 h-4" />}
    {children}
  </button>
);

// Status Badge Component
const StatusBadge = ({ status }) => {
  const styles = {
    connected: 'bg-green-100 text-green-700',
    disconnected: 'bg-gray-100 text-gray-600',
    not_connected: 'bg-gray-100 text-gray-600',
    requires_reauth: 'bg-amber-100 text-amber-700',
    syncing: 'bg-blue-100 text-blue-700',
    success: 'bg-green-100 text-green-700',
    partial: 'bg-amber-100 text-amber-700',
    failed: 'bg-red-100 text-red-700',
    never_synced: 'bg-gray-100 text-gray-600'
  };

  const labels = {
    connected: 'Connected',
    disconnected: 'Disconnected',
    not_connected: 'Not Connected',
    requires_reauth: 'Re-auth Required',
    syncing: 'Syncing...',
    success: 'Success',
    partial: 'Partial',
    failed: 'Failed',
    never_synced: 'Never Synced'
  };

  return (
    <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${styles[status] || styles.disconnected}`}>
      {labels[status] || status}
    </span>
  );
};

// Connection Tab Component
const ConnectionTab = ({ status, onConnect, onDisconnect, onRefresh, loading }) => {
  const isConnected = status?.status === 'connected';
  
  return (
    <div className="space-y-6">
      {/* Connection Status Card */}
      <div className="bg-white rounded-lg border p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-xl ${isConnected ? 'bg-green-100' : 'bg-gray-100'}`}>
              <Cloud className={`w-6 h-6 ${isConnected ? 'text-green-600' : 'text-gray-400'}`} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-lg">Azure DevOps</h3>
              <p className="text-sm text-gray-500 mt-1">
                {isConnected 
                  ? 'Connected and ready to sync work items'
                  : 'Connect your Azure DevOps organization to sync work items'}
              </p>
              <div className="mt-3">
                <StatusBadge status={status?.status || 'not_connected'} />
              </div>
            </div>
          </div>
          
          <button
            onClick={onRefresh}
            disabled={loading}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh status"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        
        {/* Connection Details */}
        {isConnected && status?.profile && (
          <div className="mt-6 pt-6 border-t border-gray-100">
            <h4 className="text-sm font-medium text-gray-700 mb-4">Connection Details</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Connected As</span>
                <p className="font-medium text-gray-900 mt-1">{status.profile.display_name || 'Unknown'}</p>
              </div>
              <div>
                <span className="text-gray-500">Email</span>
                <p className="font-medium text-gray-900 mt-1">{status.profile.email || 'N/A'}</p>
              </div>
              <div>
                <span className="text-gray-500">Organizations</span>
                <p className="font-medium text-gray-900 mt-1">{status.accounts?.length || 0} accessible</p>
              </div>
              <div>
                <span className="text-gray-500">Token Expires</span>
                <p className="font-medium text-gray-900 mt-1">
                  {status.token_expires_at 
                    ? new Date(status.token_expires_at).toLocaleString()
                    : 'Unknown'}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Last Refreshed</span>
                <p className="font-medium text-gray-900 mt-1">
                  {status.last_token_refresh
                    ? new Date(status.last_token_refresh).toLocaleString()
                    : 'Never'}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Connected Since</span>
                <p className="font-medium text-gray-900 mt-1">
                  {status.connected_at
                    ? new Date(status.connected_at).toLocaleDateString()
                    : 'Unknown'}
                </p>
              </div>
            </div>
            
            {/* Scopes */}
            <div className="mt-4">
              <span className="text-gray-500 text-sm">Authorized Scopes</span>
              <div className="flex flex-wrap gap-2 mt-2">
                {(status.scopes || []).map((scope, idx) => (
                  <span key={idx} className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                    {scope}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
        
        {/* Actions */}
        <div className="mt-6 pt-6 border-t border-gray-100 flex gap-3">
          {isConnected ? (
            <button
              onClick={onDisconnect}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
            >
              <Unlink className="w-4 h-4" />
              Disconnect
            </button>
          ) : (
            <button
              onClick={onConnect}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Link2 className="w-4 h-4" />
              )}
              Connect to Azure DevOps
            </button>
          )}
        </div>
      </div>
      
      {/* OAuth Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900">OAuth2 Authentication</h4>
            <p className="text-sm text-blue-700 mt-1">
              ScopeOps uses OAuth2 to securely connect to Azure DevOps. Tokens are encrypted at rest
              and automatically refreshed before expiry. You can revoke access at any time.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Project Mapping Tab Component
const ProjectMappingTab = ({ status, mappings, onCreateMapping, onDeleteMapping, onSync, loading }) => {
  const [adoOrg, setAdoOrg] = useState('');
  const [adoProjects, setAdoProjects] = useState([]);
  const [scopeopsProjects, setScopeopsProjects] = useState([]);
  const [selectedAdoProject, setSelectedAdoProject] = useState('');
  const [selectedScopeOpsProject, setSelectedScopeOpsProject] = useState('');
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [syncingMapping, setSyncingMapping] = useState(null);
  // E2E mapping fields (Sprint 11)
  const [adoTeam, setAdoTeam] = useState('');
  const [adoAreaPath, setAdoAreaPath] = useState('');
  const [defaultWorkItemType, setDefaultWorkItemType] = useState('Epic');
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  const isConnected = status?.status === 'connected';
  
  // Load ADO projects when organization is entered
  const loadAdoProjects = async () => {
    if (!adoOrg) return;
    setLoadingProjects(true);
    try {
      const token = localStorage.getItem('simpliview_token');
      const res = await axios.get(`${BACKEND_URL}/api/integrations/ado/projects?organization=${adoOrg}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdoProjects(res.data.projects || []);
    } catch (err) {
      toast.error('Failed to load ADO projects');
      setAdoProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };
  
  // Load ScopeOps projects
  const loadScopeOpsProjects = async () => {
    try {
      const token = localStorage.getItem('simpliview_token');
      const res = await axios.get(`${BACKEND_URL}/api/integrations/ado/scopeops-projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScopeopsProjects(res.data.projects || []);
    } catch (err) {
      console.error('Failed to load ScopeOps projects:', err);
    }
  };
  
  useEffect(() => {
    if (isConnected) {
      loadScopeOpsProjects();
      // Pre-fill organization from accounts
      if (status?.accounts?.length > 0) {
        setAdoOrg(status.accounts[0].accountName);
      }
    }
  }, [isConnected, status?.accounts]);
  
  const handleCreateMapping = async () => {
    if (!adoOrg || !selectedAdoProject || !selectedScopeOpsProject) {
      toast.error('Please select both ADO and ScopeOps projects');
      return;
    }
    
    try {
      await onCreateMapping({
        ado_organization: adoOrg,
        ado_project: selectedAdoProject,
        scopeops_project_id: selectedScopeOpsProject,
        ado_team: adoTeam || null,
        ado_area_path: adoAreaPath || null,
        default_work_item_type: defaultWorkItemType
      });
      setSelectedAdoProject('');
      setSelectedScopeOpsProject('');
      setAdoTeam('');
      setAdoAreaPath('');
      setDefaultWorkItemType('Epic');
      toast.success('Project mapping created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create mapping');
    }
  };
  
  const handleSync = async (mappingId) => {
    setSyncingMapping(mappingId);
    try {
      await onSync(mappingId);
      toast.success('Sync started');
    } catch (err) {
      toast.error('Sync failed');
    } finally {
      setSyncingMapping(null);
    }
  };
  
  if (!isConnected) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
        <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
        <h3 className="font-medium text-amber-900">Connection Required</h3>
        <p className="text-sm text-amber-700 mt-1">
          Please connect to Azure DevOps first before setting up project mappings.
        </p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Create Mapping Form */}
      <div className="bg-white rounded-lg border p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Create New Mapping</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* ADO Organization */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              ADO Organization
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={adoOrg}
                onChange={(e) => setAdoOrg(e.target.value)}
                placeholder="e.g., contoso"
                className="flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              <button
                onClick={loadAdoProjects}
                disabled={!adoOrg || loadingProjects}
                className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                {loadingProjects ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Load'}
              </button>
            </div>
          </div>
          
          {/* ADO Project */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              ADO Project
            </label>
            <select
              value={selectedAdoProject}
              onChange={(e) => setSelectedAdoProject(e.target.value)}
              disabled={adoProjects.length === 0}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="">Select ADO project...</option>
              {adoProjects.map((p) => (
                <option key={p.id} value={p.name}>{p.name}</option>
              ))}
            </select>
          </div>
          
          {/* ScopeOps Project */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              ScopeOps Project
            </label>
            <select
              value={selectedScopeOpsProject}
              onChange={(e) => setSelectedScopeOpsProject(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="">Select ScopeOps project...</option>
              {scopeopsProjects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Advanced E2E Mapping Options */}
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            {showAdvanced ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            Advanced Options
          </button>
          
          {showAdvanced && (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg border-dashed border">
              {/* ADO Team */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  ADO Team <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  type="text"
                  value={adoTeam}
                  onChange={(e) => setAdoTeam(e.target.value)}
                  placeholder="e.g., MyTeam"
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              
              {/* Area Path */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Area Path <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  type="text"
                  value={adoAreaPath}
                  onChange={(e) => setAdoAreaPath(e.target.value)}
                  placeholder="e.g., Project\\Team"
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              
              {/* Default Work Item Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Default Work Item Type
                </label>
                <select
                  value={defaultWorkItemType}
                  onChange={(e) => setDefaultWorkItemType(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="Epic">Epic</option>
                  <option value="Feature">Feature</option>
                  <option value="User Story">User Story</option>
                  <option value="Bug">Bug</option>
                  <option value="Task">Task</option>
                </select>
              </div>
            </div>
          )}
        </div>
        
        <div className="mt-4 flex justify-end">
          <button
            onClick={handleCreateMapping}
            disabled={!selectedAdoProject || !selectedScopeOpsProject}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Link2 className="w-4 h-4" />
            Create Mapping
          </button>
        </div>
      </div>
      
      {/* Existing Mappings */}
      <div className="bg-white rounded-lg border">
        <div className="px-6 py-4 border-b">
          <h3 className="font-semibold text-gray-900">Active Mappings</h3>
        </div>
        
        {mappings.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <FolderSync className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            <p>No project mappings configured yet.</p>
          </div>
        ) : (
          <div className="divide-y">
            {mappings.map((mapping) => (
              <div key={mapping.id} className="p-4 hover:bg-gray-50" data-testid={`mapping-${mapping.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 text-sm">
                      <Cloud className="w-4 h-4 text-blue-500" />
                      <span className="font-medium">{mapping.ado_organization}/{mapping.ado_project}</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                    <div className="flex items-center gap-2 text-sm">
                      <Folder className="w-4 h-4 text-teal-500" />
                      <span className="font-medium">{mapping.scopeops_project_name}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <StatusBadge status={mapping.last_sync_status || 'never_synced'} />
                    {mapping.last_sync_at && (
                      <span className="text-xs text-gray-500">
                        Last sync: {new Date(mapping.last_sync_at).toLocaleString()}
                      </span>
                    )}
                    <button
                      onClick={() => handleSync(mapping.id)}
                      disabled={syncingMapping === mapping.id}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-teal-600 bg-teal-50 rounded-lg hover:bg-teal-100 transition-colors"
                    >
                      {syncingMapping === mapping.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Play className="w-4 h-4" />
                      )}
                      Sync Now
                    </button>
                    <button
                      onClick={() => onDeleteMapping(mapping.id)}
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {/* E2E Mapping Details */}
                {(mapping.ado_team || mapping.ado_area_path || mapping.default_work_item_type) && (
                  <div className="mt-2 pl-6 flex flex-wrap gap-3 text-xs text-gray-500">
                    {mapping.ado_team && (
                      <span className="px-2 py-1 bg-blue-50 text-blue-600 rounded">Team: {mapping.ado_team}</span>
                    )}
                    {mapping.ado_area_path && (
                      <span className="px-2 py-1 bg-purple-50 text-purple-600 rounded">Area: {mapping.ado_area_path}</span>
                    )}
                    <span className="px-2 py-1 bg-teal-50 text-teal-600 rounded">Type: {mapping.default_work_item_type || 'Epic'}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Sync History Tab Component
const SyncHistoryTab = ({ history, loading, onRefresh }) => {
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-gray-900">Sync History</h3>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>
      
      <div className="bg-white rounded-lg border">
        {history.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <History className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            <p>No sync history yet. Start a sync to see results here.</p>
          </div>
        ) : (
          <div className="divide-y">
            {history.map((record, idx) => (
              <div key={record._id || idx} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <StatusBadge status={record.status} />
                    <span className="text-sm font-medium text-gray-900">
                      {record.sync_type === 'full' ? 'Full Sync' : 'Incremental Sync'}
                    </span>
                    {record.dry_run && (
                      <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">
                        Dry Run
                      </span>
                    )}
                  </div>
                  <span className="text-sm text-gray-500">
                    {record.completed_at ? new Date(record.completed_at).toLocaleString() : 'In progress'}
                  </span>
                </div>
                
                <div className="mt-3 grid grid-cols-5 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Pulled</span>
                    <p className="font-medium text-gray-900">{record.records_pulled || 0}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Created</span>
                    <p className="font-medium text-green-600">{record.records_created || 0}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Updated</span>
                    <p className="font-medium text-blue-600">{record.records_updated || 0}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Skipped</span>
                    <p className="font-medium text-gray-600">{record.records_skipped || 0}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Failed</span>
                    <p className="font-medium text-red-600">{record.records_failed || 0}</p>
                  </div>
                </div>
                
                {record.errors && record.errors.length > 0 && (
                  <div className="mt-3 p-3 bg-red-50 rounded-lg">
                    <span className="text-xs font-medium text-red-700">Errors:</span>
                    <ul className="mt-1 text-xs text-red-600 list-disc list-inside">
                      {record.errors.slice(0, 3).map((err, i) => (
                        <li key={i}>ID {err.ado_id}: {err.error}</li>
                      ))}
                      {record.errors.length > 3 && (
                        <li>... and {record.errors.length - 3} more</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Main Component
const AzureDevOpsSettings = () => {
  const [activeTab, setActiveTab] = useState('connection');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [history, setHistory] = useState([]);
  
  // Get auth token with tenant isolation
  const getAuthHeaders = () => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('simpliview_token')}`
    };
    const tenantId = localStorage.getItem('impersonated_tenant_id') || localStorage.getItem('simpliview_user_tenant_id');
    if (tenantId) {
      headers['X-Tenant-ID'] = tenantId;
    }
    return headers;
  };
  
  // Load connection status
  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/integrations/ado/status`, {
        headers: getAuthHeaders()
      });
      setStatus(res.data);
    } catch (err) {
      console.error('Failed to load ADO status:', err);
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Load project mappings
  const loadMappings = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/integrations/ado/mappings`, {
        headers: getAuthHeaders()
      });
      setMappings(res.data.mappings || []);
    } catch (err) {
      console.error('Failed to load mappings:', err);
    }
  }, []);
  
  // Load sync history
  const loadHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/integrations/ado/sync/history`, {
        headers: getAuthHeaders()
      });
      setHistory(res.data.history || []);
    } catch (err) {
      console.error('Failed to load sync history:', err);
    }
  }, []);
  
  // Initial load
  useEffect(() => {
    loadStatus();
    loadMappings();
    loadHistory();
  }, [loadStatus, loadMappings, loadHistory]);
  
  // Listen for OAuth callback
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'ado_oauth_complete') {
        if (event.data.status === 'success') {
          toast.success('Azure DevOps connected successfully!');
          loadStatus();
          loadMappings();
        } else {
          toast.error(`Connection failed: ${event.data.error}`);
        }
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [loadStatus, loadMappings]);
  
  // Connect handler
  const handleConnect = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/integrations/ado/connect`, {}, {
        headers: getAuthHeaders()
      });
      
      // Open OAuth popup
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      
      window.open(
        res.data.auth_url,
        'ado_oauth',
        `width=${width},height=${height},left=${left},top=${top}`
      );
    } catch (err) {
      toast.error('Failed to initiate connection');
    } finally {
      setLoading(false);
    }
  };
  
  // Disconnect handler
  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Azure DevOps?')) return;
    
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/integrations/ado/disconnect`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Azure DevOps disconnected');
      setStatus({ status: 'not_connected' });
      setMappings([]);
    } catch (err) {
      toast.error('Failed to disconnect');
    } finally {
      setLoading(false);
    }
  };
  
  // Create mapping handler
  const handleCreateMapping = async (data) => {
    await axios.post(`${BACKEND_URL}/api/integrations/ado/map`, data, {
      headers: getAuthHeaders()
    });
    loadMappings();
  };
  
  // Delete mapping handler
  const handleDeleteMapping = async (mappingId) => {
    if (!window.confirm('Delete this project mapping?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/integrations/ado/mappings/${mappingId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Mapping deleted');
      loadMappings();
    } catch (err) {
      toast.error('Failed to delete mapping');
    }
  };
  
  // Sync handler
  const handleSync = async (mappingId) => {
    await axios.post(
      `${BACKEND_URL}/api/integrations/ado/sync/${mappingId}`,
      { sync_type: 'incremental', dry_run: false },
      { headers: getAuthHeaders() }
    );
    setTimeout(() => {
      loadMappings();
      loadHistory();
    }, 2000);
  };
  
  return (
    <div className="space-y-6" data-testid="ado-settings-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2.5 bg-blue-100 rounded-xl">
          <Cloud className="w-6 h-6 text-blue-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Azure DevOps Integration</h1>
          <p className="text-sm text-gray-500">Connect and sync work items with Azure DevOps</p>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex gap-1">
          <Tab 
            active={activeTab === 'connection'} 
            onClick={() => setActiveTab('connection')}
            icon={Link2}
          >
            Connection
          </Tab>
          <Tab 
            active={activeTab === 'mapping'} 
            onClick={() => setActiveTab('mapping')}
            icon={FolderSync}
          >
            Project Mapping
          </Tab>
          <Tab 
            active={activeTab === 'history'} 
            onClick={() => setActiveTab('history')}
            icon={History}
          >
            Sync History
          </Tab>
        </div>
      </div>
      
      {/* Tab Content */}
      <div>
        {activeTab === 'connection' && (
          <ConnectionTab
            status={status}
            onConnect={handleConnect}
            onDisconnect={handleDisconnect}
            onRefresh={loadStatus}
            loading={loading}
          />
        )}
        
        {activeTab === 'mapping' && (
          <ProjectMappingTab
            status={status}
            mappings={mappings}
            onCreateMapping={handleCreateMapping}
            onDeleteMapping={handleDeleteMapping}
            onSync={handleSync}
            loading={loading}
          />
        )}
        
        {activeTab === 'history' && (
          <SyncHistoryTab
            history={history}
            loading={loading}
            onRefresh={loadHistory}
          />
        )}
      </div>
    </div>
  );
};

export default AzureDevOpsSettings;
